import React from 'react';
import moment from 'moment';

/**
 * Content audit trail — Created By / Last Edited By.
 *
 * Every read endpoint for Series, Categories, Genres, Tags and Banner Management
 * returns created_by_name / created_at and updated_by_name / updated_at. Records
 * that pre-date the feature come back with null names, which must render as a
 * plain "—" rather than looking broken.
 */

const DASH = '—';

export const formatAuditName = (name) => (name && String(name).trim() ? String(name).trim() : DASH);

export const formatAuditDate = (date) => {
    if (!date) return DASH;
    const m = moment(date);
    return m.isValid() ? m.format('MMM DD, YYYY · HH:mm') : DASH;
};

/** Escapes text destined for a DataTables `render` string. */
const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

/** Renders one "Name / timestamp" stack for a jQuery DataTables cell. */
export const renderAuditCellHtml = (name, date) => `
    <div class="audit-cell">
        <span class="audit-cell-name">${escapeHtml(formatAuditName(name))}</span>
        <span class="audit-cell-date">${escapeHtml(formatAuditDate(date))}</span>
    </div>`;

/** DataTables column definitions for the two audit columns. */
export const auditDataTableColumns = () => ([
    {
        data: null,
        title: 'Created By',
        orderable: false,
        render: (data, type, row) => renderAuditCellHtml(row?.created_by_name, row?.created_at),
    },
    {
        data: null,
        title: 'Last Edited By',
        orderable: false,
        render: (data, type, row) => renderAuditCellHtml(row?.updated_by_name, row?.updated_at),
    },
]);

/** Inline React version, for card/detail layouts. */
export const AuditStamp = ({ label, name, date, className = '' }) => (
    <div className={`audit-stamp ${className}`.trim()}>
        <span className="audit-stamp-label">{label}</span>
        <span className="audit-stamp-name">{formatAuditName(name)}</span>
        <span className="audit-stamp-date">{formatAuditDate(date)}</span>
    </div>
);

/** Both stamps for a record, ready to drop into a card footer or detail panel. */
export const AuditTrail = ({ record, className = '' }) => (
    <div className={`audit-trail ${className}`.trim()}>
        <AuditStamp label="Created by" name={record?.created_by_name} date={record?.created_at} />
        <AuditStamp label="Last edited by" name={record?.updated_by_name} date={record?.updated_at} />
    </div>
);

/** Shared styles — mounted once by whichever screen renders an audit trail. */
export const AuditTrailStyles = () => (
    <style>{`
        .audit-trail {
            display: flex; flex-wrap: wrap; gap: 14px;
            padding-top: 10px; margin-top: 10px;
            border-top: 1px solid rgba(138,138,154,0.18);
        }
        .audit-stamp { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .audit-stamp-label {
            font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #8a8a9a;
        }
        .audit-stamp-name {
            font-size: 12.5px; font-weight: 600; color: var(--body-text-color, #c8c8d8);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .audit-stamp-date { font-size: 11px; color: #8a8a9a; }
        .audit-cell { display: flex; flex-direction: column; gap: 1px; }
        .audit-cell-name { font-size: 13px; }
        .audit-cell-date { font-size: 11px; color: #8a8a9a; }
    `}</style>
);

export default AuditTrail;
